import React, { useState, useEffect } from 'react';
import { Calendar, Eye, Edit, Download, Plus, X } from 'lucide-react';
import { PDFViewer, pdf } from '@react-pdf/renderer';
import supabase from '../../supabase';
import EmpSalaryEdit from './EmpSalaryEdit';
import EmpSalarypdf from './EmpSalarypdf';
import './EmpSalarySlip.css';

const EmpSalarySlip = ({ employee }) => {
  // Remove useParams - get employeeId from props instead
  const employeeId = employee?.id;
  
  // Debug logging
  console.log('EmpSalarySlip - received employee:', employee);
  console.log('EmpSalarySlip - extracted employeeId:', employeeId);
  
  // Remove employee state since it's passed as prop
  const [company, setCompany] = useState(null);
  const [paySlips, setPaySlips] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear] = useState(new Date().getFullYear());
  
  // States for managing edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedPaySlip, setSelectedPaySlip] = useState(null);
  
  // States for PDF viewing
  const [isPDFViewMode, setIsPDFViewMode] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // State to track if current month payslip exists and generation status
  const [currentMonthPaySlipExists, setCurrentMonthPaySlipExists] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Detect if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || 
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (employeeId && employee) {
      fetchCompanyData();
      fetchPaySlips();
    }
  }, [employeeId, selectedYear, employee]);

  // Check if current month payslip exists whenever paySlips change
  useEffect(() => {
    checkCurrentMonthPaySlip();
  }, [paySlips, currentMonth, currentYear]);

  const checkCurrentMonthPaySlip = () => {
    const exists = paySlips.some(slip => 
      slip.month === currentMonth && slip.year === currentYear
    );
    setCurrentMonthPaySlipExists(exists);
  };

  // Separate function to fetch only company data since employee is passed as prop
  const fetchCompanyData = async () => {
    try {
      // Fetch company data using employee's user_id
      const { data: companyData, error: companyError } = await supabase
        .from('users')
        .select(`
          company_name,
          address,
          gst_number,
          logo_url,
          subscription_id
        `)
        .eq('id', employee.user_id)
        .single();
      
      if (companyError) throw companyError;
      setCompany(companyData);
    } catch (error) {
      console.error('Error fetching company data:', error);
    }
  };

  const fetchPaySlips = async () => {
    try {
      setLoading(true);
      
      // Fetch all pay slips for this employee to get available years
      const { data: allPaySlips, error: allError } = await supabase
        .from('pay_slips')
        .select('year, month')
        .eq('employee_id', employeeId)
        .order('year', { ascending: false });
      
      if (allError) throw allError;
      
      // Get unique years from existing pay slips
      const existingYears = [...new Set(allPaySlips.map(slip => slip.year))];
      
      // Always include current year and previous 2 years for selection
      const defaultYears = [currentYear, currentYear - 1, currentYear - 2];
      
      // Combine and sort years (remove duplicates)
      const allYears = [...new Set([...existingYears, ...defaultYears])].sort((a, b) => b - a);
      
      setAvailableYears(allYears);
      
      // Fetch pay slips for selected year
      const { data: yearPaySlips, error } = await supabase
        .from('pay_slips')
        .select('*')
        .eq('employee_id', employeeId)
        .eq('year', selectedYear)
        .order('month', { ascending: false });
      
      if (error) throw error;
      setPaySlips(yearPaySlips || []);
    } catch (error) {
      console.error('Error fetching pay slips:', error);
      setPaySlips([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get days in current month
  const getDaysInCurrentMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  };

  // New function to create default payslip data (FIXED - removed created_at/updated_at)
  const createDefaultPaySlipData = () => {
    const defaultEarnings = employee.earnings || {
      'Basic Salary': Math.round(employee.salary * 0.5) || 0,
      'House Rent Allowance': Math.round(employee.salary * 0.2) || 0,
      'Travel Allowance': Math.round(employee.salary * 0.1) || 0,
      'Performance Pay': Math.round(employee.salary * 0.2) || 0
    };
    
    const defaultDeductions = employee.deductions || {
      'Professional Tax': 200
    };
    
    const daysInCurrentMonth = getDaysInCurrentMonth();
    
    // Calculate total earnings and deductions
    const totalEarnings = Object.values(defaultEarnings).reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
    const totalDeductions = Object.values(defaultDeductions).reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
    const totalAmount = totalEarnings - totalDeductions;
    
    return {
      employee_id: employeeId,
      user_id: employee.user_id,
      earnings: defaultEarnings,
      deductions: defaultDeductions,
      month: currentMonth,
      year: currentYear,
      std_days: daysInCurrentMonth,
      working_days: daysInCurrentMonth,
      no_of_leaves: employee.leave_days || 0,
      total_amount: totalAmount,
      transaction_date: new Date().toISOString().split('T')[0]
      // Removed created_at and updated_at - let Supabase handle these automatically
    };
  };

  // Enhanced generate function that does everything automatically (FIXED)
  const handleGenerate = async () => {
    if (!employee || !company || currentMonthPaySlipExists || isGenerating) {
      return;
    }

    try {
      setIsGenerating(true);
      
      // Step 1: Create default payslip data
      const paySlipData = createDefaultPaySlipData();
      
      // Step 2: Save to Supabase
      const { data: savedPaySlip, error: saveError } = await supabase
        .from('pay_slips')
        .insert(paySlipData)
        .select()
        .single();
      
      if (saveError) {
        console.error('Error saving payslip:', saveError);
        alert('Error generating salary slip. Please try again.');
        return;
      }
      
      console.log('Payslip saved successfully:', savedPaySlip);
      
      // Step 3: Refresh payslips list
      await fetchPaySlips();
      
      // Step 4: Prepare PDF data and download
      const pdfPayloadData = {
        employee,
        company,
        paySlip: savedPaySlip
      };
      
      // Step 5: Generate and download PDF
      setTimeout(async () => {
        try {
          const pdfDoc = pdf(<EmpSalarypdf {...pdfPayloadData} />);
          const pdfBlob = await pdfDoc.toBlob();
          
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${employee.name}_${monthNames[currentMonth - 1]}_${currentYear}_Payslip.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          console.log('PDF downloaded successfully');
        } catch (pdfError) {
          console.error('Error generating PDF:', pdfError);
          alert('Salary slip saved but PDF download failed. You can download it from the table below.');
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error in handleGenerate:', error);
      alert('Error generating salary slip. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const fetchPaySlipData = async (paySlip) => {
    try {
      setPdfLoading(true);
      
      // Ensure we have employee and company data
      if (!employee || !company) {
        console.error('Employee or company data not available');
        return;
      }
      
      // If paySlip is provided, use it; otherwise create data for current month
      let paySlipData;
      
      if (paySlip) {
        paySlipData = paySlip;
      } else {
        // Check if there's an existing payslip for current month
        const { data: existingPaySlip, error: paySlipError } = await supabase
          .from('pay_slips')
          .select('*')
          .eq('employee_id', employeeId)
          .eq('month', currentMonth)
          .eq('year', currentYear)
          .single();
        
        if (existingPaySlip && !paySlipError) {
          paySlipData = existingPaySlip;
        } else {
          // Create default payslip data for current month using employee data
          paySlipData = createDefaultPaySlipData();
        }
      }
      
      // Set the PDF data with proper structure
      const pdfPayload = {
        employee,
        company,
        paySlip: paySlipData
      };
      
      setPdfData(pdfPayload);
      
      // Generate PDF blob for mobile/universal viewing
      try {
        const pdfDoc = pdf(<EmpSalarypdf {...pdfPayload} />);
        const blob = await pdfDoc.toBlob();
        setPdfBlob(blob);
      } catch (error) {
        console.error('Error generating PDF blob:', error);
      }
      
    } catch (error) {
      console.error('Error fetching payslip data:', error);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleView = async (paySlip = null) => {
    if (!employee || !company) {
      console.error('Employee or company data not loaded yet');
      return;
    }
    
    await fetchPaySlipData(paySlip);
    setIsPDFViewMode(true);
  };

  const handleEdit = (paySlip) => {
    setSelectedPaySlip(paySlip);
    setIsEditMode(true);
  };

  const handleDownload = async (paySlip) => {
    try {
      setPdfLoading(true);
      await fetchPaySlipData(paySlip);
      
      // Wait for pdfData to be set
      setTimeout(async () => {
        if (pdfData) {
          const pdfDoc = pdf(<EmpSalarypdf {...pdfData} />);
          const pdfBlob = await pdfDoc.toBlob();
          
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${employee.name}_${monthNames[paySlip ? paySlip.month - 1 : currentMonth - 1]}_${paySlip ? paySlip.year : currentYear}_Payslip.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
        setPdfLoading(false);
      }, 500);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setPdfLoading(false);
    }
  };

  const handleBackFromEdit = async (savedPaySlip = null) => {
    setIsEditMode(false);
    setSelectedPaySlip(null);
    
    // Refresh the pay slips list after returning from edit
    await fetchPaySlips();
    
    // If a payslip was saved (not just cancelled), automatically download PDF
    if (savedPaySlip) {
      try {
        // Small delay to ensure the payslip is properly saved and fetched
        setTimeout(async () => {
          await handleDownload(savedPaySlip);
        }, 1000);
      } catch (error) {
        console.error('Error auto-downloading PDF after save:', error);
      }
    }
  };

  const handleClosePDFView = () => {
    setIsPDFViewMode(false);
    setPdfData(null);
    setPdfBlob(null);
  };

  // Mobile PDF view handler - opens PDF in new tab
  const handleMobilePDFView = () => {
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
      // Clean up URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  // Early returns for error states
  if (!employee) {
    return (
      <div className="EmpSalarySlip-container">
        <div className="EmpSalarySlip-loading">Employee data not provided</div>
      </div>
    );
  }

  if (!employeeId) {
    return (
      <div className="EmpSalarySlip-container">
        <div className="EmpSalarySlip-loading">Employee ID not found</div>
      </div>
    );
  }

  if (loading && !company) {
    return (
      <div className="EmpSalarySlip-container">
        <div className="EmpSalarySlip-loading">Loading...</div>
      </div>
    );
  }

  // If in edit mode, show the EmpSalaryEdit component
  if (isEditMode) {
    return (
      <EmpSalaryEdit 
        employeeId={employeeId}
        paySlipId={selectedPaySlip?.id || 'new'}
        onBack={handleBackFromEdit}
        onSave={handleBackFromEdit} // Pass the same function to handle save
      />
    );
  }

  return (
    <div className="EmpSalarySlip-container">
      {/* PDF Viewer Overlay */}
      {isPDFViewMode && pdfData && (
        <div className="EmpSalarySlip-pdf-overlay">
          <div className="EmpSalarySlip-pdf-container">
            <div className="EmpSalarySlip-pdf-header">
              <h3>Salary Slip - {pdfData.employee.name}</h3>
              <div className="EmpSalarySlip-pdf-header-actions">
                {isMobile && pdfBlob && (
                  <button 
                    className="EmpSalarySlip-pdf-mobile-btn"
                    onClick={handleMobilePDFView}
                    title="Open in new tab"
                  >
                    Open PDF
                  </button>
                )}
                <button 
                  className="EmpSalarySlip-pdf-close"
                  onClick={handleClosePDFView}
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="EmpSalarySlip-pdf-viewer">
              {pdfLoading ? (
                <div className="EmpSalarySlip-pdf-loading">Generating PDF...</div>
              ) : isMobile ? (
                <div className="EmpSalarySlip-mobile-pdf-message">
                  <div className="EmpSalarySlip-mobile-pdf-content">
                    <h4>PDF Ready</h4>
                    <p>Click "Open PDF" button above to view the salary slip in a new tab, or download it directly.</p>
                    {pdfBlob && (
                      <div className="EmpSalarySlip-mobile-pdf-actions">
                        <button 
                          className="EmpSalarySlip-mobile-action-btn"
                          onClick={handleMobilePDFView}
                        >
                          View PDF
                        </button>
                        <button 
                          className="EmpSalarySlip-mobile-action-btn"
                          onClick={() => {
                            const url = URL.createObjectURL(pdfBlob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `${employee.name}_${monthNames[(pdfData.paySlip.month || currentMonth) - 1]}_${pdfData.paySlip.year || currentYear}_Payslip.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                          }}
                        >
                          Download PDF
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <PDFViewer width="100%" height="100%">
                  <EmpSalarypdf {...pdfData} />
                </PDFViewer>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Current Month Card */}
      <div className="EmpSalarySlip-current-month-card">
        <div className="EmpSalarySlip-card-header">
          <div className="EmpSalarySlip-card-title-section">
            <Calendar className="EmpSalarySlip-card-icon" />
            <div>
              <h2 className="EmpSalarySlip-card-title">
                {monthNames[currentMonth - 1]} {currentYear}
              </h2>
              <p className="EmpSalarySlip-card-subtitle">Current Month</p>
            </div>
          </div>
          <div className="EmpSalarySlip-card-actions">
            <button 
              className="EmpSalarySlip-action-btn EmpSalarySlip-btn-view"
              onClick={() => handleView()}
              disabled={pdfLoading || !employee || !company}
            >
              <Eye size={16} />
              {pdfLoading ? 'Loading...' : 'View'}
            </button>
            <button 
              className="EmpSalarySlip-action-btn EmpSalarySlip-btn-edit"
              onClick={() => handleEdit()}
            >
              <Edit size={16} />
              Edit
            </button>
            <button 
              className={`EmpSalarySlip-action-btn EmpSalarySlip-btn-generate ${(currentMonthPaySlipExists || isGenerating) ? 'disabled' : ''}`}
              onClick={handleGenerate}
              disabled={currentMonthPaySlipExists || isGenerating}
              title={currentMonthPaySlipExists ? 'Salary slip for current month already exists' : 'Generate salary slip for current month'}
            >
              <Plus size={16} />
              {isGenerating ? 'Generating...' : currentMonthPaySlipExists ? 'Generated' : 'Generate'}
            </button>
          </div>
        </div>
      </div>

      {/* Pay Slips Table */}
      <div className="EmpSalarySlip-table-container">
        <div className="EmpSalarySlip-table-wrapper">
          {loading ? (
            <div className="EmpSalarySlip-loading">Loading pay slips...</div>
          ) : paySlips.length === 0 ? (
            <div className="EmpSalarySlip-no-data">
              <p>No salary slips found for {selectedYear}</p>
            </div>
          ) : (
            <table className="EmpSalarySlip-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Year</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paySlips.map((paySlip) => (
                  <tr key={paySlip.id}>
                    <td className="EmpSalarySlip-month-cell">
                      {monthNames[paySlip.month - 1]}
                    </td>
                    <td>{paySlip.year}</td>
                    <td className="EmpSalarySlip-actions-cell">
                      <div className="EmpSalarySlip-table-actions">
                        <button
                          className="EmpSalarySlip-table-btn EmpSalarySlip-btn-view-sm"
                          onClick={() => handleView(paySlip)}
                          title="View"
                          disabled={pdfLoading}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="EmpSalarySlip-table-btn EmpSalarySlip-btn-edit-sm"
                          onClick={() => handleEdit(paySlip)}
                          title="Edit"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="EmpSalarySlip-table-btn EmpSalarySlip-btn-download-sm"
                          onClick={() => handleDownload(paySlip)}
                          title="Download"
                          disabled={pdfLoading}
                        >
                          <Download size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmpSalarySlip;